import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonIcon from '@mui/icons-material/Person';
import { Alert, Box, Button, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { type CohortProfileView } from '@verantos-dev/data-access-cohorts';
import {
  ArtifactDetailHeader,
  Button as WaitButton,
  Icon,
  type ProjectDetailHeaderMetaItem,
} from '@verantos-dev/shared-components';
import { DatasetDetailsLink } from '@verantos-dev/ui-cohort-builder-components';

import { useCohortDetailBreadcrumbs } from './useCohortDetailBreadcrumbs';
import { useCohortDetailHeader } from './useCohortDetailHeader';

interface CohortDetailHeaderProps {
  cohortProfile: CohortProfileView | undefined;
  isLoading: boolean;
}

// Live lifecycle status → unified asset-status vocabulary; mirrors the
// backend cohort_to_asset_status mapper.
const COHORT_STATUS_TO_ASSET_STATUS: Record<string, string> = {
  DRAFT: 'draft',
  UNSAVED: 'draft',
  PROCESSING: 'processing',
  PROCESSED: 'ready',
  FINAL: 'ready',
  ERROR: 'error',
};

/**
 * Gen-3 cohort detail header: breadcrumb, title + status chip, actions,
 * and the type • subjects • owner • last modified meta row — rendered
 * through the shared ArtifactDetailHeader.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
export const CohortDetailHeader = ({ cohortProfile, isLoading }: CohortDetailHeaderProps) => {
  const theme = useTheme();
  const intl = useIntl();
  const {
    cohortName,
    ownerFirstName,
    ownerLastName,
    status,
    subjectCount,
    lastModifiedDate,
    datasetReleaseId,
    datasetReleaseName,
    actions,
    processError,
  } = useCohortDetailHeader({
    cohortProfile,
  });
  const breadcrumbs = useCohortDetailBreadcrumbs(cohortName, isLoading);

  const ownerName =
    ownerFirstName && ownerLastName
      ? `${ownerFirstName} ${ownerLastName}`
      : intl.formatMessage({
          id: 'projects.cohortBuilderPage.components.cohortInfoBar.unknownOwner',
        });

  // Mirror useCohortMetadata's `date | time` format; guard the undefined
  // date (intl.formatDate(undefined) would render "Invalid Date").
  const lastModified = lastModifiedDate
    ? `${intl.formatDate(lastModifiedDate, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })} | ${intl.formatTime(lastModifiedDate, { hour: 'numeric', minute: '2-digit' })}`
    : undefined;

  // Right-aligned action buttons on the title row (Process / Save / …).
  const rightContent = (
    <Box
      data-testid="cohort-detail-header-actions"
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
    >
      {actions.map((action) =>
        // TODO: cleanup with skip_process_confirmation_modal ff
        action.waitButton !== undefined ? (
          <Tooltip key={action.id} title={action.tooltipText ?? ''} arrow>
            <span>
              <WaitButton
                id={action.id}
                btnType={action.btnType}
                size="small"
                waitButton={action.waitButton}
                displayLabel={action.label}
                disabled={!!action.disabled}
                onClickHandler={(id, e) => action.onClickHandler?.(id, e)}
              />
            </span>
          </Tooltip>
        ) : (
          <Tooltip key={action.id} title={action.tooltipText ?? ''} arrow>
            <span>
              <Button
                id={action.id}
                data-testid={action.id}
                variant={action.btnType === 'primary' ? 'contained' : 'outlined'}
                size="small"
                disabled={!!action.disabled}
                onClick={(e) => action.onClickHandler?.(action.id, e)}
              >
                {action.label}
              </Button>
            </span>
          </Tooltip>
        )
      )}
    </Box>
  );

  // Unified asset-status vocabulary; an unmapped/legacy status falls back to
  // 'draft' so a missing status never renders as usable.
  const effectiveStatus = status
    ? COHORT_STATUS_TO_ASSET_STATUS[status.toUpperCase()] ?? 'draft'
    : undefined;

  const metaItems = (
    [
      {
        key: 'type',
        icon: (
          <GroupsOutlinedIcon
            data-testid="cohort-detail-header-type-icon"
            sx={{ fontSize: 18, color: 'text.secondary' }}
          />
        ),
        label: `${intl.formatMessage({
          id: 'projects.cohortBuilderPage.components.cohortInfoBar.labelType',
        })}:`,
        value: intl.formatMessage({
          id: 'projects.cohortBuilderPage.components.cohortInfoBar.typeCohort',
        }),
      },
      {
        // Plain icon/label/value item (not the legacy CohortSubjects, whose
        // built-in right margin breaks the meta row's uniform 24px gaps).
        key: 'subjects',
        // MUI Person (the Figma "user-solid" glyph) — the shared Icon's
        // subjectsCount path is drawn on a 14-unit grid inside a 24 viewBox
        // and renders tiny/offset.
        icon: (
          <PersonIcon
            data-testid="cohort-detail-header-subjects-icon"
            sx={{ fontSize: 18, color: 'text.secondary' }}
          />
        ),
        label: `${intl.formatMessage({
          id: 'projects.cohortBuilderPage.components.cohortInfoBar.subjects',
        })}:`,
        value: (
          <Typography variant="body2" data-testid="cohort-detail-header-subject-count">
            {subjectCount != null && subjectCount >= 0 ? intl.formatNumber(subjectCount) : '--'}
          </Typography>
        ),
      },
      {
        key: 'owner',
        icon: (
          <Icon
            id="cohort-detail-header-owner-icon"
            icon="owner"
            width={16}
            height={16}
            sx={{ fill: theme.palette.text.secondary }}
          />
        ),
        label: `${intl.formatMessage({
          id: 'projects.cohortBuilderPage.components.cohortInfoBar.labelOwner',
        })}:`,
        value: (
          <Typography variant="body2" data-testid="cohort-detail-header-owner-name">
            {isLoading ? <Skeleton variant="text" width={120} /> : ownerName}
          </Typography>
        ),
      },
      {
        key: 'lastModified',
        icon: (
          <AccessTimeOutlinedIcon
            data-testid="cohort-detail-header-last-modified-icon"
            sx={{ fontSize: 18, color: 'text.secondary' }}
          />
        ),
        label: `${intl.formatMessage({
          id: 'projects.cohortBuilderPage.components.cohortInfoBar.labelLastModified',
        })}:`,
        value: (
          <Typography variant="body2" data-testid="cohort-detail-header-last-modified">
            {isLoading ? <Skeleton variant="text" width={140} /> : lastModified ?? '—'}
          </Typography>
        ),
      },
    ] as (ProjectDetailHeaderMetaItem | null)[]
  ).filter((item): item is ProjectDetailHeaderMetaItem => item !== null);

  return (
    <>
      <ArtifactDetailHeader
        id="cohort-detail-breadcrumb-header"
        breadcrumbs={breadcrumbs}
        title={cohortName}
        isLoading={isLoading}
        status={effectiveStatus}
        statusTestId="cohort-detail-header-status"
        rightContent={rightContent}
        // The dataset the cohort was built on, as the line under the title — it
        // qualifies every number in the meta row rather than sitting beside them
        // as another attribute, and opens the same details the project header does.
        subtitle={
          <DatasetDetailsLink
            releaseId={datasetReleaseId}
            releaseName={datasetReleaseName}
            testId="cohort-detail-header-dataset"
          />
        }
        metaItems={metaItems}
      />
      {/* TODO: cleanup with skip_process_confirmation_modal ff */}
      {processError && (
        <Alert severity="error" data-testid="cohort-detail-process-error">
          {intl.formatMessage({ id: 'projects.cohortBuilderPage.header.processError' })}
        </Alert>
      )}
    </>
  );
};
